import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import { ajax } from 'cadenza/api-client/api';
import type { DataProtectionMode } from 'cadenza/api-client/dto/data-protection-mode';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { DatabaseRepositorySchemaDto } from 'cadenza/repository/model/database-repository-schema-dto';
import type { ReportLayoutName } from 'cadenza/repository/model/report-layout-name.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RepositorySchemaId } from 'cadenza/repository/repository-schema-id';
import type { ItemType } from 'cadenza/repository/model/item-type.gen';

// TODO Replace with a RepositoryDto sub-type ("MutableRepositoryDto")
/** @see net/disy/cadenza/repository/web/rest/dto/CreateRepositoryDto.java */
interface CreateRepositoryDto {
  repositorySchemaId: RepositorySchemaId;
  printName: string;
  isPublishingProcessEnabled: boolean;
  isProjectsEnabled: boolean;
  dataProtectionMode: DataProtectionMode;
  dpcWorkbooksEnabled: boolean;
  dpcProjectsEnabled: boolean;
  deletionDeadlineWorkbookEnabled: boolean;
  deletionDeadlineProjectsEnabled: boolean;
}

// TODO Replace with a RepositoryDto sub-type ("MutableRepositoryDto")
interface UpdateRepositoryDto {
  printName: string;
  defaultReportLayout: ReportLayoutName | null;
}

class RepositoryService {
  list(
    filterPrivilege?: ObjectPrivilege,
    filterItemType?: ItemType
  ) {
  const params = new URLSearchParams();
  if (filterPrivilege) {
    params.append('filter.privilege', filterPrivilege);
  }
  if (filterItemType) {
    params.append('filter.hasItemType', filterItemType);
  }
    return ajax<RepositoryDto[]>(`/repositories/?${params}`);
  }

  listVisible(): Promise<RepositoryDto[]> {
    return this.list('visible');
  }

  listWritable(): Promise<RepositoryDto[]> {
    return this.list('modifyContent');
  }

  listSchemas() {
    return ajax<DatabaseRepositorySchemaDto[]>('/databaseRepositorySchemas');
  }

  create(repo: CreateRepositoryDto): Promise<void> {
    return ajax<void>('/repositories', {
      type: 'POST',
      contentType: MIMETYPE_JSON,
      data: JSON.stringify(repo),
    });
  }

  get(name: RepositoryName): Promise<RepositoryDto> {
    return ajax<RepositoryDto>(`/repositories/${name}`);
  }

  patch(name: RepositoryName, patch: Partial<UpdateRepositoryDto>) {
    return ajax<void>(`/repositories/${name}`, {
      method: 'PATCH',
      contentType: MIMETYPE_JSON,
      data: JSON.stringify(patch),
    });
  }

  delete(name: RepositoryName): Promise<void> {
    return ajax<void>(`/repositories/${name}`, { type: 'DELETE' });
  }
}

const instance = new RepositoryService();

export function repositoryService() {
  return instance;
}
